<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.courseRun.actions.addMakeupCourseRuns.title')"
    :info="$t('components.courseRun.actions.addMakeupCourseRuns.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.add'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <o-form ref="form">
        <o-field-group
          :label="$t('components.courseRun.actions.addMakeupCourseRuns.viewAllLabel')"
          :info="$t('components.courseRun.actions.addMakeupCourseRuns.viewAllInfo')"
          col
          reverse
          class="mb-4 block"
        >
          <toggle-field v-model="viewAll" />
        </o-field-group>

        <o-field-group
          v-if="!viewAll"
          :label="$t('components.courseRun.actions.addMakeupCourseRuns.courseLabel')"
          :name="$t('components.courseRun.actions.addMakeupCourseRuns.courseLabel')"
          :info="$t('components.courseRun.actions.addMakeupCourseRuns.courseInfo')"
          class="mb-4 block"
        >
          <list-box-field
            v-model="course"
            :options="courseOptions"
            :placeholder="$t('components.courseRun.actions.addMakeupCourseRuns.selectCourse')"
            class="w-full"
          />
        </o-field-group>

        <o-field-group
          v-if="viewAll || course"
          :label="$t('components.courseRun.actions.addMakeupCourseRuns.runsLabel')"
          :name="$t('components.courseRun.actions.addMakeupCourseRuns.runsLabel')"
          class="mb-4 block"
        >
          <div class="block -mx-4 border-t border-b h-80 bg-gray-100 dark:bg-gray-900 dark:border-gray-800 overflow-hidden">
            <div
              v-if="loadingRuns"
              class="flex h-full items-center justify-center gap-2 text-sm text-gray-700 dark:text-gray-200"
              role="status"
            >
              <o-loader size="20px" />
              <span>{{ $t('components.courseRun.actions.addMakeupCourseRuns.loading') }}</span>
            </div>
            <checkbox-group-field
              v-else-if="courseRunOptions.length"
              v-model="selected"
              :options="courseRunOptions"
              search
              class="h-80"
            />
            <p v-else class="text-sm opacity-70 text-center p-5">
              {{ $t('components.courseRun.actions.addMakeupCourseRuns.noRuns') }}
            </p>
          </div>
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script>
import {
  CheckboxGroupField,
  ListBoxField,
  ToggleField
} from '@/components/fields'

import { CourseRunStatus } from '@/_api/models/course'

export default {
  components: {
    CheckboxGroupField,
    ListBoxField,
    ToggleField
  },
  props: {
    portal: {
      type: String,
      default: undefined
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      currentCourseRun: null,
      currentSelected: [],
      courses: [],
      course: null,
      courseRuns: [],
      selected: [],
      loadingRuns: false,
      viewAll: false,
      latestRunsRequest: 0
    }
  },
  computed: {
    courseOptions () {
      return this.courses.map(course => ({
        value: course,
        label: course.name
      }))
    },
    currentSelectedIds () {
      return this.currentSelected.map(r => r.idCourseRun)
    },
    courseRunOptions () {
      return this.courseRuns
        .filter(run => run.idCourseRun !== this.currentCourseRun?.idCourseRun)
        .filter(run => !this.currentSelectedIds.includes(run.idCourseRun))
        .filter(run => run.status !== CourseRunStatus.cancelled)
        .map(run => ({
          value: run,
          text: `${run.course?.name ?? this.course?.name ?? ''} - ${run.information}`,
          info: run.dates?.[0] ? this.$date.convert(run.dates[0], undefined, this.$date.presets.long) : ''
        }))
    }
  },
  watch: {
    course () {
      if (!this.viewAll) {
        this.fetchCourseRuns()
      }
    },
    viewAll () {
      this.course = null
      this.fetchCourseRuns()
    }
  },
  methods: {
    async open (courseRun, currentSelected = []) {
      this.latestRunsRequest += 1
      this.currentCourseRun = courseRun
      this.currentSelected = currentSelected
      this.selected = []
      this.course = null
      this.courseRuns = []
      this.viewAll = false
      this.loadingRuns = false

      await this.fetchCourses()

      this.$refs.dialog.open()
    },
    confirm () {
      this.$emit('update', this.selected)
      this.$refs.dialog.close()
    },
    validate () {
      return this.selected.length > 0
    },
    async fetchCourses () {
      try {
        const courses = await this.$api.course.list()
        this.courses = courses.items
      } catch (e) {
        console.error(e)
      }
    },
    async fetchCourseRuns () {
      const requestId = ++this.latestRunsRequest
      this.courseRuns = []
      this.selected = []

      if (!this.viewAll && !this.course) {
        this.loadingRuns = false
        return
      }

      this.loadingRuns = true
      try {
        const filters = {
          status: 'active',
          when: 'current_upcoming',
        }

        if (!this.viewAll) {
          filters.course = this.course.idCourse
        }

        const runs = await this.$api.course.listCourseRuns({
          page: 1,
          filters,
        })

        if (requestId !== this.latestRunsRequest) return

        this.courseRuns = runs.items
      } catch (e) {
        if (requestId !== this.latestRunsRequest) return

        console.error(e)
      } finally {
        if (requestId === this.latestRunsRequest) {
          this.loadingRuns = false
        }
      }
    }
  }
}
</script>
